// @ts-check
import * as THREE from 'three';
import { createScene } from '../core/scene.js';
import { createRenderer, handleResize } from '../core/renderer.js';
import { createCamera } from '../core/camera.js';
import { FirstPersonControls } from '../core/controls.js';
import { Lighting } from './Lighting.js';
import { Maze } from './Maze.js';
import { VideoPainting } from './VideoPainting.js';
import { UI } from './UI.js';
import { AssetLoader } from '../utils/AssetLoader.js';
import { PostProcessing } from '../utils/PostProcessing.js';
import { MazeGenerator } from '../utils/MazeGenerator.js';
import { ConfigGenerator } from '../utils/ConfigGenerator.js';
import { resolveSeed, setSeedInUrl } from '../utils/SeedResolver.js';
import { IntroMessage } from './IntroMessage.js';
import { Minimap } from './Minimap.js';
import { getAnalyticsService } from '../utils/AnalyticsService.js';
import mazeSettings from '../config/maze-settings.json';

/**
 * @typedef {import('../utils/types.js').MazeLayout} MazeLayout
 * @typedef {import('../utils/types.js').GalleryConfig} GalleryConfig
 * @typedef {import('../utils/types.js').Vector3Like} Vector3Like
 */

export class Gallery {
  constructor(canvas) {
    this.canvas = canvas;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.lighting = null;
    this.maze = null;
    this.paintings = [];
    this.ui = null;
    this.assetLoader = null;
    this.postProcessing = null;
    this.audioListener = null;
    /** @type {GalleryConfig | null} */
    this.config = null;
    this.introMessage = null;
    this.startPosition = null;
    this.minimap = null;
    /** @type {MazeLayout | null} */
    this.mazeLayout = null;
    /** @type {number | null} */
    this.seed = null; // Resolved seed driving both maze layout and video shuffle
    this.seedFromUrl = false; // Whether this session's seed came from a shared link

    this.clock = new THREE.Clock();
    this.isRunning = false;

    // Dynamic video loading
    this.currentRoomId = null;
    this.loadedRooms = new Set(); // Track which rooms have loaded videos
    this.loadDistance = 1; // Load videos for current room + 1 room away
    this.lastLoadCheck = 0; // Timestamp of last load check
    this.maxConcurrentVideoLoads = 3; // Videos downloading at once - see loadVideosInOrder()
    this.videoLoadToken = 0; // Bumped per room change to abandon a superseded load queue
    /** @type {Map<string, VideoPainting>} */
    this.paintingsBySlotId = new Map(); // slotId -> VideoPainting, built in init()
  }

  async init() {
    // Initialize UI
    this.ui = new UI();
    this.ui.updateLoadingText('Initializing gallery...');

    // Initialize asset loader
    this.assetLoader = new AssetLoader();
    this.assetLoader.setOnProgress((progress) => {
      // this.ui is assigned synchronously above and never reassigned, so
      // it's already set by the time this async callback fires - the
      // checker can't see that guarantee across the closure boundary.
      /** @type {UI} */ (this.ui).updateLoadingProgress(progress);
    });

    // Load configuration (procedural or static)
    try {
      this.ui.updateLoadingText('Loading configuration...');

      // Use imported maze settings (bundled during build)
      // Check if procedural generation is enabled
      if (mazeSettings.enableProcedural) {
        this.ui.updateLoadingText('Generating procedural maze...');
        console.log('Procedural generation enabled');

        // Load video manifest
        let videoList;
        try {
          videoList = await ConfigGenerator.loadVideoManifest(
            mazeSettings.videoManifestPath || '/videos/manifest.json'
          );
        } catch (e) {
          console.warn('Video manifest not found, using fallback videos');
          // TODO: mazeSettings has no `paintingCount` key today (verified by
          // reading maze-settings.json - it isn't in the schema), so this
          // always falls back to 9 in practice. Dead/mismatched config read,
          // pre-existing and out of scope to fix here - either add
          // `paintingCount` to maze-settings.json or delete this read.
          // (Found by the strictNullChecks/checkJs pass in story 002; the
          // cast below documents the mismatch instead of silently making it
          // type-check, so it stays visible rather than disappearing.)
          const fallbackCount = /** @type {{ paintingCount?: number }} */ (mazeSettings).paintingCount || 9;
          videoList = ConfigGenerator.generateFallbackVideoList(fallbackCount);
        }

        // Determine painting count
        const paintingCount = videoList.length;
        console.log(`Generating maze for ${paintingCount} paintings`);

        // Resolve the one seed that drives both the maze layout and the video
        // shuffle (URL param -> maze-settings.json -> Date.now()). Resolving it
        // once here, instead of once per generator, is what makes a maze shareable:
        // both generators always agree, even when the seed came from a link.
        const { seed, source } = resolveSeed(mazeSettings, window.location.search);
        this.seed = seed;
        this.seedFromUrl = source === 'url';
        console.log(`Resolved seed: ${seed} (source: ${source})`);

        // Generate maze layout
        const generator = new MazeGenerator({
          paintingCount: paintingCount,
          cellSize: mazeSettings.cellSize,
          corridorWidth: mazeSettings.corridorWidth,
          roomPadding: mazeSettings.roomPadding,
          seed: seed,
          minPaintingDistance: mazeSettings.minPaintingDistance,
          minPaintingsPerRoom: mazeSettings.minPaintingsPerRoom || 1,
          maxPaintingsPerRoom: mazeSettings.maxPaintingsPerRoom || 3,
          minEntrancesPerRoom: mazeSettings.minEntrancesPerRoom || 1,
          maxEntrancesPerRoom: mazeSettings.maxEntrancesPerRoom || 3
        });

        this.mazeLayout = generator.generate();

        // Validate painting spacing
        generator.validatePaintingSpacing();

        // Generate final configuration
        this.config = ConfigGenerator.generateFromVideos(videoList, this.mazeLayout, {
          defaultScale: mazeSettings.defaultScale,
          shuffleVideos: mazeSettings.shuffleVideos,
          seed: seed
        });

        // Log generation info
        console.log('Procedural generation complete:', {
          seed: this.mazeLayout.seed,
          gridSize: this.mazeLayout.gridSize,
          totalSize: this.mazeLayout.totalSize,
          paintings: this.config.paintings.length,
          walls: this.config.walls.length
        });

        // Optionally save generated config for debugging
        if (mazeSettings.debug?.enabled) {
          console.log('Generated config:', this.config);
        }

        // Initialize analytics service if configured
        if (mazeSettings.analytics?.enabled && mazeSettings.analytics?.workerUrl) {
          getAnalyticsService(mazeSettings.analytics.workerUrl);
          console.log('Analytics service initialized');
        }
      } else {
        // Load static configuration
        this.ui.updateLoadingText('Loading static configuration...');
        this.config = await this.assetLoader.loadConfig('/src/config/gallery-config.json');
        console.log('Using static configuration');

        // Initialize analytics service if configured
        if (mazeSettings.analytics?.enabled && mazeSettings.analytics?.workerUrl) {
          getAnalyticsService(mazeSettings.analytics.workerUrl);
          console.log('Analytics service initialized');
        }
      }
    } catch (error) {
      console.error('Failed to load configuration:', error);
      this.ui.updateLoadingText('Error loading configuration');
      return;
    }

    // Create Three.js components
    this.ui.updateLoadingText('Creating 3D scene...');
    this.scene = createScene();
    this.camera = createCamera();
    this.renderer = createRenderer(this.canvas);

    // Create audio listener and attach to camera
    this.audioListener = new THREE.AudioListener();
    this.camera.add(this.audioListener);
    console.log('Audio listener created and attached to camera');

    // Set up post-processing
    this.postProcessing = new PostProcessing(this.renderer, this.scene, this.camera);

    // Handle window resize
    handleResize(this.camera, this.renderer);
    window.addEventListener('resize', () => {
      // this.postProcessing is assigned synchronously above and never
      // reassigned, so it's already set by the time this event callback
      // fires - the checker can't see that guarantee across the closure
      // boundary.
      /** @type {PostProcessing} */ (this.postProcessing).setSize(window.innerWidth, window.innerHeight);
    });

    // Create lighting system
    this.ui.updateLoadingText('Setting up lighting...');
    this.lighting = new Lighting(this.scene);

    // Create maze
    this.ui.updateLoadingText('Building maze...');
    this.maze = new Maze(this.scene, this.config);

    // Set up controls
    this.ui.updateLoadingText('Configuring controls...');
    this.controls = new FirstPersonControls(this.camera, this.renderer.domElement);
    // this.maze is assigned synchronously above and never reassigned, so
    // it's already set by the time this callback runs (from
    // FirstPersonControls' own update loop) - the checker can't see that
    // guarantee across the closure boundary.
    this.controls.checkCollision = (position) => /** @type {Maze} */ (this.maze).checkCollision(position);

    // this.config is assigned once earlier in init() (procedural or static
    // branch) and never reassigned - non-null for the rest of this method.
    const config = /** @type {GalleryConfig} */ (this.config);

    // Set camera to start position
    this.startPosition = { ...config.startPosition };
    this.camera.position.set(
      config.startPosition.x,
      config.startPosition.y,
      config.startPosition.z
    );

    // Skip video preloading - videos will load dynamically based on room proximity
    this.ui.updateLoadingText('Preparing video gallery...');

    // Create paintings
    this.ui.updateLoadingText('Creating paintings...');
    config.paintings.forEach((paintingData) => {
      const painting = new VideoPainting(this.scene, paintingData, this.lighting, this.audioListener);
      this.paintings.push(painting);
      // Index by slotId (layout identity) for id-based room lookups - see getPaintingsInRooms()
      if (paintingData.slotId) {
        this.paintingsBySlotId.set(paintingData.slotId, painting);
      }
    });

    // Create intro message floating in front of player
    this.introMessage = new IntroMessage(this.scene, this.startPosition, 0);

    // Create minimap if maze data is available
    if (this.mazeLayout) {
      this.minimap = new Minimap(this.mazeLayout);

      // this.seed is only ever null before the procedural-generation branch
      // above runs; this.mazeLayout is only ever set inside that same
      // branch, right after this.seed is resolved - so this.seed is
      // non-null whenever this.mazeLayout is. The checker can't see that
      // cross-field, cross-branch correlation, and can't see across the
      // onShareToggle closure below either way.
      const seed = /** @type {number} */ (this.seed);

      // Wire up the shareable-maze-URL toggle. If this session itself was
      // loaded from a shared link, reflect the toggle as already on instead
      // of defaulting to off.
      this.ui.setShareEnabled(this.seedFromUrl);
      if (this.seedFromUrl) {
        setSeedInUrl(true, seed);
      }
      this.ui.onShareToggle((enabled) => {
        setSeedInUrl(enabled, seed);
      });
    }

    // Show start options and wait for user to click start
    this.ui.showStartOptions();

    // Set up start button callback
    this.ui.onStart(() => {
      // this.ui is assigned synchronously at the top of init() and never
      // reassigned, so it's already set by the time this click callback
      // fires - the checker can't see that guarantee across the closure
      // boundary.
      /** @type {UI} */ (this.ui).hideLoadingScreen();
      this.start();
    });

    console.log('Gallery initialized successfully');
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;

    // Load initial videos for starting room
    this.updateVideoLoading().then(() => {
      console.log('Initial video loading complete');
    });

    this.animate();
  }

  stop() {
    this.isRunning = false;
  }

  animate() {
    if (!this.isRunning) return;

    requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();
    // const elapsedTime = this.clock.getElapsedTime(); // Not needed without post-processing

    // animate() only ever runs after init() has completed (isRunning is
    // set true in start(), itself only reachable from the onStart callback
    // registered at the end of init()), by which point every component
    // field below has been assigned and is never reset to null - the
    // checker can't see that ordering guarantee across method boundaries.

    // Update controls
    /** @type {FirstPersonControls} */ (this.controls).update(delta);

    // Update video loading based on room position (non-blocking)
    // Only check every ~1 second to avoid excessive checks
    if (!this.lastLoadCheck || Date.now() - this.lastLoadCheck > 1000) {
      this.lastLoadCheck = Date.now();
      this.updateVideoLoading().catch(err => {
        console.error('Error updating video loading:', err);
      });
    }

    // Update intro message (fade out when player moves)
    if (this.introMessage && this.introMessage.isVisible) {
      this.introMessage.checkPlayerDistance(this.camera.position, this.startPosition, 2);
      const stillVisible = this.introMessage.update(delta);
      if (!stillVisible) {
        this.introMessage.dispose();
        this.introMessage = null;
      }
    }

    // Update paintings (proximity-based video playback with visibility check)
    this.paintings.forEach((painting) => {
      painting.update(this.camera);
    });

    // Update minimap
    if (this.minimap && /** @type {UI} */ (this.ui).isMinimapEnabled()) {
      this.minimap.updatePlayer(
        this.camera.position.x,
        this.camera.position.z,
        this.camera.rotation.y
      );
      this.minimap.draw();
    }

    // Update UI (painting info)
    // NOTE: Painting info is now displayed as 3D text on the wall next to each painting
    // No need to update HTML overlay
    // if (this.controls.isLocked) {
    //   this.ui.updatePaintingInfo(this.camera.position, this.paintings, this.camera, this.renderer);
    // }

    // Update post-processing effects
    // this.postProcessing.update(elapsedTime);

    // Render scene with post-processing (DISABLED - using direct render)
    // this.postProcessing.render();

    // Direct rendering without post-processing effects
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Get the room ID that contains the given position
   * Returns null if not in any room or if no maze layout
   *
   * @param {Vector3Like} position
   * @returns {string | null}
   */
  getRoomAtPosition(position) {
    if (!this.mazeLayout || !this.mazeLayout.rooms) {
      return null;
    }

    for (const room of this.mazeLayout.rooms) {
      // Check if position is within room bounds
      if (position.x >= room.x && position.x <= room.x + room.width &&
          position.z >= room.z && position.z <= room.z + room.depth) {
        return room.id;
      }
    }

    return null; // In a corridor or outside
  }

  /**
   * Get all rooms connected to the given room ID
   *
   * @param {string} roomId
   * @returns {Array<string>}
   */
  getAdjacentRooms(roomId) {
    if (!this.mazeLayout || !this.mazeLayout.rooms || !this.mazeLayout.corridors) {
      return [];
    }

    const adjacentRoomIds = new Set();

    // Find all corridors connected to this room
    for (const corridor of this.mazeLayout.corridors) {
      if (corridor.from === roomId) {
        adjacentRoomIds.add(corridor.to);
      } else if (corridor.to === roomId) {
        adjacentRoomIds.add(corridor.from);
      }
    }

    return Array.from(adjacentRoomIds);
  }

  /**
   * Get all rooms within the specified distance (in room hops)
   *
   * @param {string | null} roomId
   * @param {number} distance
   * @returns {Array<string>}
   */
  getRoomsWithinDistance(roomId, distance) {
    if (!roomId || distance < 0) {
      return [];
    }

    const roomsToLoad = new Set([roomId]);
    let currentLevel = [roomId];

    for (let i = 0; i < distance; i++) {
      const nextLevel = [];
      for (const currentRoomId of currentLevel) {
        const adjacent = this.getAdjacentRooms(currentRoomId);
        for (const adjRoom of adjacent) {
          if (!roomsToLoad.has(adjRoom)) {
            roomsToLoad.add(adjRoom);
            nextLevel.push(adjRoom);
          }
        }
      }
      currentLevel = nextLevel;
    }

    return Array.from(roomsToLoad);
  }

  /**
   * Get paintings in the specified rooms
   * Resolves each room's painting slots to live VideoPainting instances via
   * paintingsBySlotId (built in init()), keyed by the slot's stable id -
   * no coordinate comparison involved.
   *
   * @param {Array<string>} roomIds
   * @returns {Array<VideoPainting>}
   */
  getPaintingsInRooms(roomIds) {
    if (!this.mazeLayout || !this.mazeLayout.rooms) {
      return [];
    }

    const paintingsToLoad = [];

    for (const roomId of roomIds) {
      const room = this.mazeLayout.rooms.find(r => r.id === roomId);
      if (room && room.paintings) {
        for (const paintingSlot of room.paintings) {
          const painting = this.paintingsBySlotId.get(paintingSlot.id);
          if (painting) {
            paintingsToLoad.push(painting);
          }
        }
      }
    }

    return paintingsToLoad;
  }

  /**
   * Fetch view stats for a set of paintings in a single batched request.
   *
   * Called on room change alongside video loading, so the worker only ever
   * sees roughly a roomful of filenames at a time instead of one request per
   * painting for the whole gallery at startup. Paintings that already hold
   * fresh stats are skipped, and AnalyticsService's own 5-minute cache means
   * re-entering a room usually issues no request at all.
   *
   * @param {Array<VideoPainting>} paintings
   * @returns {Promise<void>}
   */
  async updateStatsLoading(paintings) {
    const analyticsService = getAnalyticsService();
    if (!analyticsService.isEnabled()) return;

    const stale = paintings.filter(p => p.needsStats());
    if (stale.length === 0) return;

    // Several slots can share one video, so dedupe before asking the worker
    const urls = Array.from(new Set(stale.map(p => p.data.videoUrl)));
    const statsByFilename = await analyticsService.getBatchStats(urls);

    for (const painting of stale) {
      const stats = statsByFilename[analyticsService.extractFilename(painting.data.videoUrl)];
      if (stats) {
        painting.applyStats(stats);
      }
    }
  }

  /**
   * Load a list of videos a few at a time instead of all at once.
   *
   * Each painting's `preload = 'auto'` element pulls the whole file down, so
   * firing a room set's worth of them concurrently saturates the connection
   * and makes the room the player is actually standing in load *slower*.
   * `paintings` must be in priority order (current room first); a small fixed
   * number of lanes walk that list in order, so nearby videos finish first
   * and further ones trickle in behind them.
   *
   * `token` guards against a stale queue: every room change bumps
   * `videoLoadToken`, and lanes from a superseded change stop pulling new work
   * rather than downloading rooms the player has already left.
   *
   * @param {Array<VideoPainting>} paintings - priority-ordered
   * @param {number} token - value of this.videoLoadToken this call belongs to
   * @returns {Promise<void>}
   */
  async loadVideosInOrder(paintings, token) {
    let next = 0;

    // Single-threaded JS: no await between the bounds check and the increment,
    // so lanes can't hand out the same painting twice.
    const lane = async () => {
      while (next < paintings.length) {
        if (token !== this.videoLoadToken) return; // superseded by a newer room change
        await paintings[next++].loadVideo();
      }
    };

    const laneCount = Math.min(this.maxConcurrentVideoLoads, paintings.length);
    await Promise.all(Array.from({ length: laneCount }, () => lane()));
  }

  /**
   * Update which videos are loaded based on player position
   *
   * @returns {Promise<void>}
   */
  async updateVideoLoading() {
    // Only do dynamic loading if we have maze layout (procedural mode)
    if (!this.mazeLayout || !this.mazeLayout.rooms) {
      // Static mode - no rooms to scope by, so every video is eventually
      // loaded, but still a few at a time rather than all at once
      if (this.loadedRooms.size === 0) {
        console.log('Static mode: loading all videos');
        // Marked loaded up front: video loading is now staggered and outlives
        // the 1s animate() check, so this is what stops a second pass from
        // re-entering mid-load.
        this.loadedRooms.add('all');

        // No rooms to scope by here, but still one batched request rather
        // than one per painting.
        this.updateStatsLoading(this.paintings).catch(err => {
          console.warn('Failed to load view stats:', err);
        });

        await this.loadVideosInOrder(this.paintings, ++this.videoLoadToken);
      }
      return;
    }

    const currentRoom = this.getRoomAtPosition(this.camera.position);

    // If we changed rooms or this is the first update, recalculate
    if (currentRoom !== this.currentRoomId) {
      this.currentRoomId = currentRoom;

      if (currentRoom) {
        console.log('Player in room:', currentRoom);

        // Get rooms to load. getRoomsWithinDistance() returns the current room
        // first, then each BFS level outwards, and getPaintingsInRooms()
        // preserves that order - so paintingsToLoad is already in the priority
        // order loadVideosInOrder() wants.
        const roomsToLoad = this.getRoomsWithinDistance(currentRoom, this.loadDistance);
        console.log('Loading videos for rooms:', roomsToLoad);

        const paintingsToLoad = this.getPaintingsInRooms(roomsToLoad);

        // Fetch view stats for the same room set - one batched request per
        // room change. Deliberately not awaited: stats are decorative, and
        // they must never delay video loading or block the room transition.
        this.updateStatsLoading(paintingsToLoad).catch(err => {
          console.warn('Failed to load view stats for rooms:', roomsToLoad, err);
        });

        // Unload before loading, not after: this frees the departed rooms'
        // bandwidth and memory before the new downloads start competing for it.
        const roomsToUnload = Array.from(this.loadedRooms).filter(
          roomId => !roomsToLoad.includes(roomId)
        );

        for (const roomId of roomsToUnload) {
          const paintingsToUnload = this.getPaintingsInRooms([roomId]);
          paintingsToUnload.forEach(p => p.unloadVideo());
        }

        // Update loaded rooms set
        this.loadedRooms.clear();
        roomsToLoad.forEach(id => this.loadedRooms.add(id));

        await this.loadVideosInOrder(paintingsToLoad, ++this.videoLoadToken);
      }
    } else if (currentRoom) {
      // Still in the same room. Re-check stats on this 1s throttle so a player
      // who lingers past the refresh window still sees counts update. Cheap:
      // updateStatsLoading() filters on needsStats() (5 minutes) and
      // AnalyticsService coalesces in-flight requests, so a lingering player
      // costs one batched request per window, not one per tick.
      this.updateStatsLoading(this.getPaintingsInRooms(Array.from(this.loadedRooms)))
        .catch(err => {
          console.warn('Failed to refresh view stats:', err);
        });
    }
  }

  dispose() {
    this.isRunning = false;

    // Stop any in-flight load lanes before the paintings they hold go away
    this.videoLoadToken++;

    // Dispose of all components
    if (this.introMessage) {
      this.introMessage.dispose();
      this.introMessage = null;
    }

    if (this.minimap) {
      this.minimap.dispose();
      this.minimap = null;
    }

    if (this.postProcessing) {
      this.postProcessing.dispose();
    }

    if (this.maze) {
      this.maze.dispose();
    }

    if (this.lighting) {
      this.lighting.dispose();
    }

    this.paintings.forEach((painting) => {
      painting.dispose();
    });
    this.paintings = [];
    this.paintingsBySlotId.clear();

    if (this.renderer) {
      this.renderer.dispose();
    }
  }
}
